/**
 * Presence detection for the native shell. There is no abstraction here on purpose.
 *
 * `window.gotimeNative` is injected by the iOS host before first paint, or it is absent and every
 * call below is a no-op. Two paths, not a capability layer: in a browser GoTime behaves exactly as
 * it always has, and nothing in the app may branch on anything finer than "is the shell here".
 */

import {
	askNotificationsMessage,
	hapticMessage,
	identityMessage,
	journeyMessage,
	parseNativeMessage,
	reminderMessage,
	watchMessage,
	type NativeHaptic,
	type NativeInbound,
	type NativeJourney,
	type NativeReminder,
	type NativeWatch,
} from "../shared/native-bridge";

interface InjectedBridge {
	version?: unknown;
	build?: unknown;
	token?: unknown;
	deviceId?: unknown;
	post?: (message: string) => void;
}

declare global {
	interface Window {
		gotimeNative?: InjectedBridge;
		gotimeWeb?: { receive: (message: string) => void };
	}
}

function bridge(): InjectedBridge | null {
	if (typeof window === "undefined") {
		return null;
	}
	const injected = window.gotimeNative;
	return injected && typeof injected.post === "function" ? injected : null;
}

export function isNativeShell(): boolean {
	return bridge() !== null;
}

/** The shell's contract version, or null in a browser. Null is not stale — it is simply absent. */
export function shellVersion(): number | null {
	const version = bridge()?.version;
	return typeof version === "number" && Number.isFinite(version) ? version : null;
}

/** Which build of the app is installed, so a phone can answer that without a Mac attached. */
export function shellBuild(): string | null {
	const build = bridge()?.build;
	return typeof build === "string" && build.length > 0 ? build : null;
}

/**
 * What the shell presents to the server in place of a push subscription.
 *
 * Readable by anything running on the page, which is the accepted cost of the page being the one
 * making the request. It buys the shell a voice in diagnostics and nothing else.
 */
export function shellToken(): string | null {
	const token = bridge()?.token;
	return typeof token === "string" && token.length > 0 ? token : null;
}

/**
 * `fetch`, carrying the shell's identity when there is one.
 *
 * Every endpoint that asks "is this a device we know" means "does it have a push subscription",
 * which the shell can never have. Anything device-gated has to go through here or it gets a 404 in
 * the app and works everywhere else — which is exactly how the voice failed.
 */
export function shellFetch(input: string, init: RequestInit = {}): Promise<Response> {
	const token = shellToken();
	if (!token) {
		return fetch(input, init);
	}
	const headers = new Headers(init.headers);
	headers.set("X-GoTime-Shell", token);
	return fetch(input, { ...init, headers });
}

export function postJourney(journey: NativeJourney): boolean {
	const target = bridge();
	if (!target?.post) {
		return false;
	}
	try {
		target.post(JSON.stringify(journeyMessage(journey)));
		return true;
	} catch {
		// A shell that cannot be spoken to changes nothing about the web app's own behaviour.
		return false;
	}
}

/** The wrist. Silent on a shell that predates the watch, and on a phone with none paired. */
export function postWatch(watch: NativeWatch): boolean {
	const target = bridge();
	if (!target?.post) {
		return false;
	}
	try {
		target.post(JSON.stringify(watchMessage(watch)));
		return true;
	} catch {
		return false;
	}
}

/**
 * How hard the buzzes land, 0 to 1.
 *
 * Kept here rather than threaded through every caller: a haptic is fired from components that have
 * no business reading preferences, and the strength is a property of the phone, not the gesture.
 */
let hapticGain = 1;

export function setHapticIntensity(value: number): void {
	hapticGain = Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : 1;
}

/** Asks the shell for a buzz. Silent in a browser, and silent on a shell that predates haptics. */
export function postHaptic(haptic: NativeHaptic): void {
	const target = bridge();
	if (!target?.post || hapticGain === 0) {
		return;
	}
	try {
		target.post(JSON.stringify(hapticMessage(haptic, hapticGain)));
	} catch {
		// A shell that cannot be spoken to changes nothing about the web app's own behaviour.
	}
}

/**
 * Asks the shell to raise a notification at an instant.
 *
 * The shell's only route to a locked phone: web push is not available in a `WKWebView`, so the
 * existing alert channel is not inherited by the app. Silent in a browser.
 */
export function postReminder(reminder: NativeReminder): void {
	const target = bridge();
	if (!target?.post) {
		return;
	}
	try {
		target.post(JSON.stringify(reminderMessage(reminder)));
	} catch {
		// A shell that cannot be spoken to changes nothing about the web app's own behaviour.
	}
}

/** Asks iOS for notification permission. Without it a push is delivered and shows nothing. */
export function postAskNotifications(): void {
	const target = bridge();
	if (!target?.post) {
		return;
	}
	try {
		target.post(JSON.stringify(askNotificationsMessage()));
	} catch {
		// A shell that cannot be spoken to changes nothing about the web app's own behaviour.
	}
}

/**
 * The identity the shell is holding, or null.
 *
 * Null on a first run, before the shell has been told one — the page's own id is handed over then
 * and kept from that point on.
 */
export function shellDeviceId(): string | null {
	const value = bridge()?.deviceId;
	return typeof value === "string" && /^[a-f0-9]{32}$/.test(value) ? value : null;
}

/** Tells the shell which device it is, so it can report on its own while the page is asleep. */
export function postIdentity(deviceId: string): void {
	const target = bridge();
	if (!target?.post) {
		return;
	}
	try {
		target.post(JSON.stringify(identityMessage(deviceId)));
	} catch {
		// A shell that cannot be spoken to changes nothing about the web app's own behaviour.
	}
}

/** Installs the receiver the shell calls into. Returns a teardown for the effect that owns it. */
export function receiveFromShell(handler: (message: NativeInbound) => void): () => void {
	if (typeof window === "undefined") {
		return () => {};
	}
	window.gotimeWeb = {
		receive: (raw: string) => {
			const message = parseNativeMessage(raw);
			if (message) {
				handler(message);
			}
		},
	};
	return () => {
		delete window.gotimeWeb;
	};
}
